import { ErpCountryValue } from '../../../common/person/model/person-values';
import {
    ErpArrivalValue, ErpBillingbasisValue, ErpReturnTripValue, ErpHealthInsuranceValue, ErpTravelManagementNotesValue
} from '../../shared/model/travelmanagement-values';
import { TravelItinerary, TravelItineraryBackend, TravelItineraryFactory } from './travelitinerary';
import { Transportation } from './transportation';
import { Assets } from './assets';
import { Document } from 'app/rm/erp/common/document/model/document';
import { getDate } from 'app/rm/erp/common/utils/date-utils';
import { EstimatedCosts } from './estimatedcosts';
import { TravelData } from './traveldata';
import { TravelAbroad, TravelAbroadBackend, TravelAbroadFactory } from './travelabroad';
import { Financing } from './financing';
import { ReimbursementCosts } from './reimbursementcosts';
import { BankDetails } from './bankdetails';
import { ErpInstitute } from 'app/rm/erp/common/institute/model/erpinstitute';
import { FurtherDetails } from './furtherdetails';
import { A1Bescheinigung } from './a1bescheinigung';

export interface BusinesstripApplicationBackend {
    id?: number;
    readonly objGuid?: string;
    readonly lockVersion?: number;
    status?: number;
    pgdJoinId?: number;
    firstname?: string;
    surname?: string;
    applicationDate?: number;

    countrySelected?: ErpCountryValue;
    destination?: string;
    travelManagementPurpose?: string;
    arrivalSelected?: ErpArrivalValue;
    returnTripSelected?: ErpReturnTripValue;
    destinationFreetext?: string;

    tripBeginDate?: number;
    tripEndDate?: number;
    businessBeginDate?: number;
    businessEndDate?: number;

    billingbasisSelected?: ErpBillingbasisValue;

    transportationExplanation?: string;
    smallMileageAllowance?: boolean;
    largeMileageAllowance?: boolean;
    largeMileageAllowanceExplanation?: string;
    isPrivateTrip?: boolean;

    transportationTrain?: boolean;
    transportationReservation?: boolean;
    transportationPublicTransport?: boolean;
    transportationMotorcycle?: boolean;
    transportationBicycle?: boolean;
    transportationAirplane?: boolean;
    transportationRentalCar?: boolean;
    transportationLongDistanceBus?: boolean;
    transportationOther?: boolean;
    transportationCompanyCar?: boolean;
    transportationOwnCar?: boolean;

    transportationReservationExplanation?: string;
    transportationOtherExplanation?: string;

    privateTripBeginDate?: number;
    privateTripEndDate?: number;
    privateTripResidence?: string;
    privateTripAddress?: string;
    privateTripExplanation?: string;

    listTravelitinerary?: TravelItineraryBackend[] | null;

    assets?: Assets;

    accommodationExpenses?: number;
    conferenceFee?: number;
    transportationCosts?: number;
    otherEstimatedCosts?: number;
    sumOfEstimatedCosts?: number;
    reductionOfEstimatedCosts?: number;

    documents?: Document[];

    listTravelAbroad?: TravelAbroadBackend[] | null;

    listFinancing?: Financing[];

    socialInsuranceNumber?: string;
    healthInsuranceSelected?: ErpHealthInsuranceValue;
    destinationDetails?: string;
    destinationsOfTheLastYears?: string;

    fullReimbursement?: boolean;
    travelAllowanceAmount?: number;
    foregoDailyAllowance?: boolean;
    foregoAccommodationAllowance?: boolean;
    foregoTravelCosts?: boolean;
    foregoParticipationFees?: boolean;
    accidentCare?: boolean;
    provideCompensationAmount?: boolean;

    accountIban?: string;
    accountBic?: string;
    changeBankDetails?: boolean;
    changeAccountNewIban?: string;
    changeAccountNewBic?: string;

    availabilityFreetext?: string;
    travelCompanionFreetext?: string;
    travelCompanionExplanationFreetext?: string;
    instituteFirstSelected?: ErpInstitute;
    instituteSecondSelected?: ErpInstitute;
    costsHigherStaedtekatalog?: boolean;
    reasonCostsHigherStaedtekatalog?: string;
    addressTravelDestination?: string;

    notes?: string;
    notesSelected?: ErpTravelManagementNotesValue;

    teachingActivityInCurriculum?: boolean;
    valueableConsiderationForTeaching?: boolean;
}

export class BusinesstripApplication {
    id?: number;
    readonly objGuid?: string;
    readonly lockVersion?: number;
    status?: number;
    pgdJoinId?: number;
    firstname?: string;
    surname?: string;
    applicationDate?: Date;

    travelData: TravelData = new TravelData();

    transportation: Transportation = new Transportation();
    listTravelitinerary?: TravelItinerary[] | null = [];
    assets: Assets = new Assets();

    estimatedCosts: EstimatedCosts = new EstimatedCosts();

    documents: Document[] = [];

    listTravelAbroad?: TravelAbroad[] | null = [];

    listFinancing?: Financing[];

    a1Bescheinigung: A1Bescheinigung = new A1Bescheinigung();

    reimbursementCosts: ReimbursementCosts = new ReimbursementCosts();

    bankDetails: BankDetails = new BankDetails();

    furtherDetails: FurtherDetails = new FurtherDetails();
}

export class BusinesstripApplicationFactory {
    static createFromBackendModel( backendModel: BusinesstripApplicationBackend ): BusinesstripApplication {
        const frontendModel: BusinesstripApplication = {
            id: backendModel.id,
            objGuid: backendModel.objGuid,
            lockVersion: backendModel.lockVersion,
            status: backendModel.status,
            pgdJoinId: backendModel.pgdJoinId,
            firstname: backendModel.firstname,
            surname: backendModel.surname,
            applicationDate: getDate( backendModel.applicationDate ),

            travelData: {
                countrySelected: backendModel.countrySelected,
                destination: backendModel.destination,
                travelManagementPurpose: backendModel.travelManagementPurpose,
                arrivalSelected: backendModel.arrivalSelected,
                returnTripSelected: backendModel.returnTripSelected,
                destinationFreetext: backendModel.destinationFreetext,

                tripBeginDate: getDate( backendModel.tripBeginDate ),
                tripEndDate: getDate( backendModel.tripEndDate ),
                businessBeginDate: getDate( backendModel.businessBeginDate ),
                businessEndDate: getDate( backendModel.businessEndDate ),

                billingbasisSelected: backendModel.billingbasisSelected,
            },

            transportation: {
                transportationExplanation: backendModel.transportationExplanation,
                smallMileageAllowance: backendModel.smallMileageAllowance,
                largeMileageAllowance: backendModel.largeMileageAllowance,
                largeMileageAllowanceExplanation: backendModel.largeMileageAllowanceExplanation,
                isPrivateTrip: backendModel.isPrivateTrip,

                transportationTrain: backendModel.transportationTrain,
                transportationReservation: backendModel.transportationReservation,
                transportationPublicTransport: backendModel.transportationPublicTransport,
                transportationMotorcycle: backendModel.transportationMotorcycle,
                transportationBicycle: backendModel.transportationBicycle,
                transportationAirplane: backendModel.transportationAirplane,
                transportationRentalCar: backendModel.transportationRentalCar,
                transportationLongDistanceBus: backendModel.transportationLongDistanceBus,
                transportationOther: backendModel.transportationOther,
                transportationCompanyCar: backendModel.transportationCompanyCar,
                transportationOwnCar: backendModel.transportationOwnCar,

                transportationReservationExplanation: backendModel.transportationReservationExplanation,
                transportationOtherExplanation: backendModel.transportationOtherExplanation,

                privateTripBeginDate: getDate( backendModel.privateTripBeginDate ),
                privateTripEndDate: getDate( backendModel.privateTripEndDate ),
                privateTripResidence: backendModel.privateTripResidence,
                privateTripAddress: backendModel.privateTripAddress,
                privateTripExplanation: backendModel.privateTripExplanation,
            },
            listTravelitinerary: backendModel.listTravelitinerary?.map( tempTravelItinerary => {
                return TravelItineraryFactory.createFromBackendModel( tempTravelItinerary );
            } ),
            assets: backendModel.assets ?? {},

            estimatedCosts: {
                accommodationExpenses: backendModel.accommodationExpenses,
                conferenceFee: backendModel.conferenceFee,
                transportationCosts: backendModel.transportationCosts,
                otherEstimatedCosts: backendModel.otherEstimatedCosts,
                sumOfEstimatedCosts: backendModel.sumOfEstimatedCosts,
                reductionOfEstimatedCosts: backendModel.reductionOfEstimatedCosts,
            },
            documents: backendModel.documents ?? [],

            listTravelAbroad: backendModel.listTravelAbroad?.map( tempTravelAbroad => {
                return TravelAbroadFactory.createFromBackendModel( tempTravelAbroad );
            } ),

            listFinancing: backendModel.listFinancing,

            a1Bescheinigung: {
                socialInsuranceNumber: backendModel.socialInsuranceNumber,
                healthInsuranceSelected: backendModel.healthInsuranceSelected,
                destinationDetails: backendModel.destinationDetails,
                destinationsOfTheLastYears: backendModel.destinationsOfTheLastYears,
            },
            reimbursementCosts: {
                fullReimbursement: backendModel.fullReimbursement,
                travelAllowanceAmount: backendModel.travelAllowanceAmount,
                foregoDailyAllowance: backendModel.foregoDailyAllowance,
                foregoAccommodationAllowance: backendModel.foregoAccommodationAllowance,
                foregoTravelCosts: backendModel.foregoTravelCosts,
                foregoParticipationFees: backendModel.foregoParticipationFees,
                accidentCare: backendModel.accidentCare,
                provideCompensationAmount: backendModel.provideCompensationAmount
            },

            bankDetails: {
                accountIban: backendModel.accountIban,
                accountBic: backendModel.accountBic,
                changeBankDetails: backendModel.changeBankDetails,
                changeAccountNewIban: backendModel.changeAccountNewIban,
                changeAccountNewBic: backendModel.changeAccountNewBic,
            },

            furtherDetails: {
                availabilityFreetext: backendModel.availabilityFreetext,
                travelCompanionFreetext: backendModel.travelCompanionFreetext,
                travelCompanionExplanationFreetext: backendModel.travelCompanionExplanationFreetext,
                instituteFirstSelected: backendModel.instituteFirstSelected,
                instituteSecondSelected: backendModel.instituteSecondSelected,
                costsHigherStaedtekatalog: backendModel.costsHigherStaedtekatalog,
                reasonCostsHigherStaedtekatalog: backendModel.reasonCostsHigherStaedtekatalog,
                addressTravelDestination: backendModel.addressTravelDestination,
                notes: backendModel.notes,
                notesSelected: backendModel.notesSelected,
                teachingActivityInCurriculum: backendModel.teachingActivityInCurriculum,
                valueableConsiderationForTeaching: backendModel.valueableConsiderationForTeaching,
            },
        };
        return frontendModel;
    }

    static createFromFrontendModel( frontendModel: BusinesstripApplication ): BusinesstripApplicationBackend {
        const backendModel: BusinesstripApplicationBackend = {
            id: frontendModel.id,
            objGuid: frontendModel.objGuid,
            lockVersion: frontendModel.lockVersion,
            status: frontendModel.status,
            pgdJoinId: frontendModel.pgdJoinId,
            firstname: frontendModel.firstname,
            surname: frontendModel.surname,
            applicationDate: frontendModel.applicationDate?.getTime(),

            countrySelected: frontendModel.travelData?.countrySelected,
            destination: frontendModel.travelData?.destination,
            travelManagementPurpose: frontendModel.travelData?.travelManagementPurpose,
            arrivalSelected: frontendModel.travelData?.arrivalSelected,
            returnTripSelected: frontendModel.travelData?.returnTripSelected,
            destinationFreetext: frontendModel.travelData?.destinationFreetext,

            tripBeginDate: frontendModel.travelData?.tripBeginDate?.getTime(),
            tripEndDate: frontendModel.travelData?.tripEndDate?.getTime(),
            businessBeginDate: frontendModel.travelData?.businessBeginDate?.getTime(),
            businessEndDate: frontendModel.travelData?.businessEndDate?.getTime(),

            billingbasisSelected: frontendModel.travelData?.billingbasisSelected,

            transportationExplanation: frontendModel.transportation?.transportationExplanation,
            smallMileageAllowance: frontendModel.transportation?.smallMileageAllowance,
            largeMileageAllowance: frontendModel.transportation?.largeMileageAllowance,
            largeMileageAllowanceExplanation: frontendModel.transportation?.largeMileageAllowanceExplanation,
            isPrivateTrip: frontendModel.transportation?.isPrivateTrip,

            transportationTrain: frontendModel.transportation?.transportationTrain,
            transportationReservation: frontendModel.transportation?.transportationReservation,
            transportationPublicTransport: frontendModel.transportation?.transportationPublicTransport,
            transportationMotorcycle: frontendModel.transportation?.transportationMotorcycle,
            transportationBicycle: frontendModel.transportation?.transportationBicycle,
            transportationAirplane: frontendModel.transportation?.transportationAirplane,
            transportationRentalCar: frontendModel.transportation?.transportationRentalCar,
            transportationLongDistanceBus: frontendModel.transportation?.transportationLongDistanceBus,
            transportationOther: frontendModel.transportation?.transportationOther,
            transportationCompanyCar: frontendModel.transportation?.transportationCompanyCar,
            transportationOwnCar: frontendModel.transportation?.transportationOwnCar,

            transportationReservationExplanation: frontendModel.transportation?.transportationReservationExplanation,
            transportationOtherExplanation: frontendModel.transportation?.transportationOtherExplanation,

            privateTripBeginDate: frontendModel.transportation?.privateTripBeginDate?.getTime(),
            privateTripEndDate: frontendModel.transportation?.privateTripEndDate?.getTime(),
            privateTripResidence: frontendModel.transportation?.privateTripResidence,
            privateTripAddress: frontendModel.transportation?.privateTripAddress,
            privateTripExplanation: frontendModel.transportation?.privateTripExplanation,

            listTravelitinerary: frontendModel.listTravelitinerary?.map( tempTravelItinerary => {
                return TravelItineraryFactory.createFromFrontendModel( tempTravelItinerary );
            } ),

            assets: Object.keys( frontendModel.assets ).length === 0 ? undefined : frontendModel.assets,

            accommodationExpenses: frontendModel.estimatedCosts.accommodationExpenses,
            conferenceFee: frontendModel.estimatedCosts.conferenceFee,
            transportationCosts: frontendModel.estimatedCosts.transportationCosts,
            otherEstimatedCosts: frontendModel.estimatedCosts.otherEstimatedCosts,
            sumOfEstimatedCosts: frontendModel.estimatedCosts.sumOfEstimatedCosts,
            reductionOfEstimatedCosts: frontendModel.estimatedCosts.reductionOfEstimatedCosts,

            documents: frontendModel.documents.length < 1 ? undefined : frontendModel.documents,

            listTravelAbroad: frontendModel.listTravelAbroad?.map( tempTravelAbroad => {
                return TravelAbroadFactory.createFromFrontendModel( tempTravelAbroad );
            } ),

            listFinancing: frontendModel.listFinancing,

            socialInsuranceNumber: frontendModel?.a1Bescheinigung?.socialInsuranceNumber,
            healthInsuranceSelected: frontendModel?.a1Bescheinigung?.healthInsuranceSelected,
            destinationDetails: frontendModel?.a1Bescheinigung?.destinationDetails,
            destinationsOfTheLastYears: frontendModel?.a1Bescheinigung?.destinationsOfTheLastYears,

            fullReimbursement: frontendModel?.reimbursementCosts?.fullReimbursement,
            travelAllowanceAmount: frontendModel?.reimbursementCosts?.travelAllowanceAmount,
            foregoDailyAllowance: frontendModel?.reimbursementCosts?.foregoDailyAllowance,
            foregoAccommodationAllowance: frontendModel?.reimbursementCosts?.foregoAccommodationAllowance,
            foregoTravelCosts: frontendModel?.reimbursementCosts?.foregoTravelCosts,
            foregoParticipationFees: frontendModel?.reimbursementCosts?.foregoParticipationFees,
            accidentCare: frontendModel?.reimbursementCosts?.accidentCare,
            provideCompensationAmount: frontendModel?.reimbursementCosts?.provideCompensationAmount,

            accountIban: frontendModel?.bankDetails?.accountIban,
            accountBic: frontendModel?.bankDetails?.accountBic,
            changeBankDetails: frontendModel?.bankDetails?.changeBankDetails,
            changeAccountNewIban: frontendModel?.bankDetails?.changeAccountNewIban,
            changeAccountNewBic: frontendModel?.bankDetails?.changeAccountNewBic,

            availabilityFreetext: frontendModel.furtherDetails.availabilityFreetext,
            travelCompanionFreetext: frontendModel.furtherDetails.travelCompanionFreetext,
            travelCompanionExplanationFreetext: frontendModel.furtherDetails.travelCompanionExplanationFreetext,
            instituteFirstSelected: frontendModel.furtherDetails.instituteFirstSelected,
            instituteSecondSelected: frontendModel.furtherDetails.instituteSecondSelected,
            costsHigherStaedtekatalog: frontendModel.furtherDetails.costsHigherStaedtekatalog,
            reasonCostsHigherStaedtekatalog: frontendModel.furtherDetails.reasonCostsHigherStaedtekatalog,
            addressTravelDestination: frontendModel.furtherDetails.addressTravelDestination,
            notes: frontendModel.furtherDetails.notes,
            notesSelected: frontendModel.furtherDetails.notesSelected,
            teachingActivityInCurriculum: frontendModel.furtherDetails.teachingActivityInCurriculum,
            valueableConsiderationForTeaching: frontendModel.furtherDetails.valueableConsiderationForTeaching,
        };
        return backendModel;
    }
}
